/* eslint-disable react/require-default-props */
// The props are optional TS props with default parameter values (the modern FC pattern); the
// react/require-default-props rule (from airbnb) is intentionally off for TS in .eslintrc, but the
// CRA dev-server ESLint doesn't pick up that override, so disable it here.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ServiceItemService from '../../../Services/ServiceItemService';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import ServiceListItem from '../../../Domain/ServiceListItem';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import EditServiceDescription from './EditServiceDescription';
import ErrorMessage from '../../../Elements/ErrorMessage';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import ServiceSearchParams from './ServiceSearchParams';
import urlHelper from '../../../Helpers/urlHelper';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import OrderDirectionsEnum from '../../../Enums/OrderDirectionsEnum';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';
import FigmaButtonRow from '../../../Elements/FigmaButtonRow';
import ChooseServicePanel from './ChooseServicePanel';
import getServiceListItemType from '../../../Domain/ServiceListItemTypeFactory';

interface ServiceItemAdminProps {
  Navigation?: boolean;
  OpportunityId?: number | string;
  StatusCode?: number;
}

const ServiceItemAdmin: React.FC<ServiceItemAdminProps> = ({ Navigation = false, OpportunityId = 0, StatusCode }) => {
  const [serviceList, setServiceList] = useState<any[]>([]);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [shownPanel, setShownPanel] = useState<string>(ShownPanelsEnum.None);
  const [allItemsInTheSearchSetAreSelected, setAllItemsInTheSearchSetAreSelected] = useState(false);
  const [searchItem, setSearchItem] = useState<any>(() => {
    // Deep-link seed for global record search (CD-5425), on the service id, which the grid's own
    // ServiceIdOrName field resolves as an id. Returned INSTEAD of the defaults rather than merged
    // into them: ServiceSearchParams defaults a one-year date range, and a service outside it would
    // be filtered straight back out of a link that points directly at it.
    // Skipped in embedded mode (Navigation), where the host scopes the list to one opportunity.
    if (!Navigation) {
      const seededServiceId = new URLSearchParams(window.location.search).get('ServiceIdOrName');

      if (seededServiceId) return { ServiceIdOrName: seededServiceId };
    }

    const tempSearchItem = new (ServiceSearchParams as any)();
    if (Navigation) tempSearchItem.OpportunityIdOrName = OpportunityId;
    return tempSearchItem;
  });

  const navigationRef = useRef(Navigation);
  navigationRef.current = Navigation;

  const refresh = useCallback(() => {
    setSearchItem((prev: any) => ({ ...prev }));
    ErrorMessage.SetLoading(false);
  }, []);

  const savePanelOnClose = useCallback(() => {
    setShownPanel(ShownPanelsEnum.None);
    refresh();
  }, [refresh]);

  const clickOnRemoveService = useCallback(
    (item: any) => {
      ErrorMessage.SetLoading(true);
      ServiceItemService.Remove(item.ServiceId)
        .then(() => savePanelOnClose())
        .catch(() => {
          ErrorMessage.SetLoading(false);
          ErrorMessage.SetErrorMessage('SIA147');
        });
    },
    [savePanelOnClose]
  );

  const clickOnServiceStatusChange = useCallback(
    (item: any) => {
      const service = { ...item };
      service.StatusChange = 'true';
      ServiceItemService.EditService(service)
        .then(() => savePanelOnClose())
        .catch(() => ErrorMessage.SetErrorMessage('SIA156'));
    },
    [savePanelOnClose]
  );

  const editGeneralFunction = useCallback((_id: any, item: any) => {
    setShownPanel(ShownPanelsEnum.Edit);
    setSelectedItem(item);
  }, []);

  const clickOnCopy = useCallback((_id: any, item: any) => {
    const serviceItem = {
      ...item,
      CopyServicePricings: 'true',
      CopyCountryPricings: false,
      IncludeNumOfEvalsPerCycle: false,
    };
    serviceItem.CopyServicePricings = 'true';
    setShownPanel(ShownPanelsEnum.Copy);
    setSelectedItem(serviceItem);
  }, []);

  const removeGeneralFunction = useCallback(
    (_id: any, item: any) => {
      ErrorMessage.SetApprove(`You will remove this Service ${item.Name}. Are you sure?`, () =>
        clickOnRemoveService(item)
      );
    },
    [clickOnRemoveService]
  );

  const statusChangeGeneralFunction = useCallback(
    (_id: any, item: any) => {
      ErrorMessage.SetApprove(`You will change the service status: ${item.Name}. Are you sure?`, () =>
        clickOnServiceStatusChange(item)
      );
    },
    [clickOnServiceStatusChange]
  );

  const clickOnServicesStatusChange = useCallback(
    (serviceListParam: any[], targetIncludedStatus: boolean) => {
      const firstService = serviceListParam[0];
      const service = { ...firstService };
      service.StatusChange = 'true';
      service.ServiceIds = serviceListParam.map((x: any) => x.ServiceId).join(',');
      service.StatusChangeTargetIncluded = `${targetIncludedStatus}`;

      ErrorMessage.SetLoading(true);
      ServiceItemService.EditService(service)
        .then(() => savePanelOnClose())
        .catch(() => {
          ErrorMessage.SetLoading(false);
          ErrorMessage.SetErrorMessage('SIA156');
        });
    },
    [savePanelOnClose]
  );

  const getSelectedServices = useCallback(() => {
    return serviceList.filter((x: any) => x.Selected === true);
  }, [serviceList]);

  const getNumberOfSelectedElements = useCallback(() => {
    return serviceList.filter((x: any) => x.Selected === true).length;
  }, [serviceList]);

  const massIncludeServices = useCallback(() => {
    const selectedServices = getSelectedServices();
    if (selectedServices.length === 0) return;
    ErrorMessage.SetApprove(`You will include ${selectedServices.length} selected service(s). Are you sure?`, () =>
      clickOnServicesStatusChange(selectedServices, true)
    );
  }, [getSelectedServices, clickOnServicesStatusChange]);

  const massExcludeServices = useCallback(() => {
    const selectedServices = getSelectedServices();
    if (selectedServices.length === 0) return;
    ErrorMessage.SetApprove(`You will exclude ${selectedServices.length} selected service(s). Are you sure?`, () =>
      clickOnServicesStatusChange(selectedServices, false)
    );
  }, [getSelectedServices, clickOnServicesStatusChange]);

  const clickOnGlobalSelect = useCallback(() => {
    const selectedStatus = serviceList.some((x: any) => x.Selected === false);
    const newServiceList = [...serviceList];
    if (newServiceList.length > 0) {
      newServiceList.forEach((x: any) => {
        x.Selected = selectedStatus;
      });
    }
    setServiceList(newServiceList);
  }, [serviceList]);

  const clickOnSelect = useCallback(
    (item: any) => {
      item.Selected = !item.Selected;
      setServiceList([...serviceList]);
    },
    [serviceList]
  );

  const selectedStatus = useCallback(() => {
    if (serviceList.length === 0) return false;
    return !serviceList.some((x: any) => x.Selected === false);
  }, [serviceList]);

  // Bind static functions on ServiceListItem
  useEffect(() => {
    (ServiceListItem as any).editGeneralFunction = editGeneralFunction;
    (ServiceListItem as any).CopyGeneralFunction = clickOnCopy;
    (ServiceListItem as any).RemoveGeneralFunction = removeGeneralFunction;
    (ServiceListItem as any).StatusChangeGeneralFunction = statusChangeGeneralFunction;
    (ServiceListItem as any).clickOnGlobalSelect = clickOnGlobalSelect;
    (ServiceListItem as any).clickOnSelect = clickOnSelect;
    (ServiceListItem as any).selectedStatus = selectedStatus;
    (ServiceListItem as any).OnFullPage = () => !navigationRef.current;
  }, [
    editGeneralFunction,
    clickOnCopy,
    removeGeneralFunction,
    statusChangeGeneralFunction,
    clickOnGlobalSelect,
    clickOnSelect,
    selectedStatus,
  ]);

  const getCreateEnabled = useCallback(() => {
    if (serviceList.length === 0) return true;
    return (
      serviceList.length > 0 &&
      !serviceList.some((item: any) => item.ClientId !== serviceList[0].ClientId) &&
      serviceList[0].OpportunityStatusCode !== 11 &&
      serviceList[0].OpportunityStatusCode !== 12
    );
  }, [serviceList]);

  const onServiceFromSubmit = useCallback(
    (item: any) => {
      const newItem = new (ServiceListItem as any)(item);
      if (Navigation) {
        newItem.OpportunityId = OpportunityId;
      } else if (searchItem.OpportunityIdOrName) {
        newItem.OpportunityId = searchItem.OpportunityIdOrName;
      }
      clickOnCopy(0, newItem);
    },
    [Navigation, OpportunityId, searchItem, clickOnCopy]
  );

  const clickOnCreate = useCallback(() => {
    const serviceItem = new (ServiceListItem as any)();
    serviceItem.OpportunityId =
      serviceList.length > 0
        ? serviceList[0].OpportunityId
        : searchItem.OpportunityIdOrName
        ? searchItem.OpportunityIdOrName
        : -1;
    if (serviceItem.OpportunityId === -1) {
      ErrorMessage.SetErrorMessage('There is no selected opportunity.');
    } else {
      setShownPanel(ShownPanelsEnum.AddNewItem);
      setSelectedItem(serviceItem);
    }
  }, [serviceList, searchItem]);

  const showPanel = useCallback((panelId: string) => {
    const resolvedPanelId = panelId === ShownPanelsEnum.Navigate ? ShownPanelsEnum.None : panelId;
    setShownPanel(resolvedPanelId);
  }, []);

  const editPanelOnClose = useCallback(
    (doRefresh?: boolean) => {
      setShownPanel(ShownPanelsEnum.None);
      if (doRefresh) refresh();
    },
    [refresh]
  );

  const renderButtons = () => {
    const href =
      OpportunityId === ''
        ? ''
        : urlHelper.getProperUrl('sales_service/search', `OpportunityIdOrName=${OpportunityId}`);

    return (
      <FigmaButtonRow
        className={Navigation ? 'InnerMenuButtonFigmaList' : 'MenuButtonList'}
        buttons={
          [
            // @ts-ignore - FigmaButtonRow accepts partial button configs
            Navigation
              ? {
                  type: ButtonTypeEnum.NormalIconOnly,
                  color: ButtonColorsEnum.Secondary,
                  iconPosition: IconPositionEnum.Center,
                  iconName: 'fa-solid fa-bell-concierge',
                  onClick: () => showPanel(ShownPanelsEnum.Navigate),
                  tooltip: 'Services',
                  roleKey: 'sales_opportunities_button',
                  href,
                }
              : null,
            {
              type: ButtonTypeEnum.NormalIconOnly,
              color: ButtonColorsEnum.Primary,
              iconPosition: IconPositionEnum.Center,
              iconName: 'fa-solid fa-copy',
              onClick: () => {
                setShownPanel(ShownPanelsEnum.CopyFrom);
              },
              tooltip: 'Copy Service From',
              roleKey: 'sales_opportunities_button',
              disabled: !getCreateEnabled() || (!Navigation && !searchItem.OpportunityIdOrName),
              hidden: OpportunityId === 0,
            },
            {
              type: ButtonTypeEnum.NormalIconOnly,
              color: ButtonColorsEnum.Primary,
              iconPosition: IconPositionEnum.Center,
              iconName: 'fa-solid fa-lock-open',
              onClick: massIncludeServices,
              tooltip: 'Mass Include Services',
              roleKey: 'opportunity_edit',
              disabled: getNumberOfSelectedElements() === 0,
            },
            {
              type: ButtonTypeEnum.NormalIconOnly,
              color: ButtonColorsEnum.Primary,
              iconPosition: IconPositionEnum.Center,
              iconName: 'fa-solid fa-lock',
              onClick: massExcludeServices,
              tooltip: 'Mass Exclude Services',
              roleKey: 'opportunity_edit',
              disabled: getNumberOfSelectedElements() === 0,
            },
            {
              type: ButtonTypeEnum.NormalIconOnly,
              color: ButtonColorsEnum.Primary,
              iconPosition: IconPositionEnum.Center,
              iconName: 'fa-solid fa-plus-circle',
              onClick: clickOnCreate,
              tooltip: 'Create Service',
              roleKey: 'sales_opportunities_button',
              disabled: !getCreateEnabled(),
              hidden: OpportunityId === 0,
            },
          ] as any
        }
      />
    );
  };

  const renderOnePanel = (panelId: string, content: React.ReactNode) => (shownPanel === panelId ? content : null);

  // Guard untyped table items before delegating to the strict factory.
  // This keeps the validation at the boundary where external data enters this component.
  const getServiceListItemTypeWithGuard = useCallback((item: any) => {
    if (typeof item?.TypeCode !== 'number') return ServiceListItem;
    return getServiceListItemType({ TypeCode: item.TypeCode });
  }, []);

  const renderPanels = () => (
    <div>
      {renderOnePanel(
        ShownPanelsEnum.AddNewItem,
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={selectedItem}
          clickOnCancel={editPanelOnClose}
          clickOnSave={savePanelOnClose}
          windowDescription={EditServiceDescription}
          errorCode={'SIA174'}
        />
      )}
      {renderOnePanel(
        ShownPanelsEnum.Edit,
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Update}
          editedItem={selectedItem}
          clickOnCancel={editPanelOnClose}
          clickOnSave={savePanelOnClose}
          windowDescription={EditServiceDescription}
          errorCode={'SIA209'}
        />
      )}
      {renderOnePanel(
        ShownPanelsEnum.Copy,
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={selectedItem}
          clickOnCancel={editPanelOnClose}
          clickOnSave={savePanelOnClose}
          windowDescription={EditServiceDescription}
          errorCode={'SIA189'}
        />
      )}
      {renderOnePanel(
        ShownPanelsEnum.CopyFrom,
        <ChooseServicePanel
          clickOnCancel={() => {
            setShownPanel(ShownPanelsEnum.None);
          }}
          clickOnSubmit={onServiceFromSubmit}
        />
      )}
    </div>
  );

  return (
    <div className='contentwrapper'>
      {renderButtons()}
      {renderPanels()}
      <TabOfDataTable
        type={ServiceListItem}
        getItemTypeFunction={getServiceListItemTypeWithGuard}
        serviceFunction={ServiceItemService.GetServiceItemList}
        convertDBItemsFunction={ServiceItemService.ConvertDBItem}
        searchItem={searchItem}
        searchParamsType={ServiceSearchParams}
        showSearchStrip={true}
        clickOnStartSearch={(item: any) => setSearchItem(item)}
        orderBy='ServiceId'
        orderDirection={OrderDirectionsEnum.Desc}
        changeOfItemListFunction={(list: any[], numberOfElementsInTheTable: number) => setServiceList(list)}
        changeOfAllItemsAreSelectedFunction={(val: boolean) => setAllItemsInTheSearchSetAreSelected(val)}
        isSelectAllDisabled={true}
        singleSearchLine={true}
        singleSearchLinePlaceholder='Search in id or name...'
        errorCode='SIA225'
      />
    </div>
  );
};

export default ServiceItemAdmin;
